import {
    createOfferSummaryCard,
    OFFER_SUMMARY_CARD_HEADLINE_CLASSNAME,
    OfferSummaryCard,
} from '@vfde-brix/ws10/offer-summary-card';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { HEADLINE_BASE_CLASSNAME } from '@vfde-brix/ws10/headline';
import { HtmlId } from '../enums/elementId';
import { mountComponent } from '../util/mount';
import {
    JarvisProduct,
    JarvisTvProduct,
} from '../interface';
import { updateFootnoteLink } from './footnoteLink';
import { getOfferSummaryCardButtonProps } from '../util/components/button.util';
import {
    getHighlightBadgeProps,
    getLegalTexts,
    getSublineText,
    getAdditionalPrices,
} from '../util/components/offerSummaryCard.util';
import { State } from '../container/App/selector';
import { getCMSConfig } from '../util/cms';
import { getElementByBrixClassName } from '../util/component';
import { addTooltipTrackingEvent } from './tooltip';
import { getOfferSummaryCardHeadlineProps } from '../util/components/headline.util';
import { getPromoPrices } from '../util/components/promoPrice.util';

/**
 * Create Brix Offer Summary Card component.
 *
 * @param id - Wrapper ID of the element.
 * @returns Offer Summary Card component
 * or null if the element with the given ID is not found.
 */
export const mountOfferSummaryCard = (id: HtmlId) =>
    mountComponent(id, createOfferSummaryCard, NO_PATTERN_BUSINESS_LOGIC);

/**
 * Append a metacode to the "data-metacode" attribute for the headline of
 * the Offer Summary Card component.
 *
 * @param offerSummaryCard - Offer Summary Card Brix component.
 * @param metacode - Metacode to display.
 */
const updateDisplayMetacode = (offerSummaryCard: OfferSummaryCard | null, metacode?: string) => {
    if (!offerSummaryCard || !metacode) {
        return;
    }

    const headlineEl = getElementByBrixClassName(offerSummaryCard, OFFER_SUMMARY_CARD_HEADLINE_CLASSNAME);

    if (headlineEl) {
        headlineEl.setAttribute('data-metacode', metacode);
    }
};

/**
 * Updates price element on the OfferSummaryCard component
 *
 * @param offerSummaryCard - The OfferSummaryCard component to update
 * @param tariff - The selected tariff
 * @param tvProduct - The availabe tv product
 * @param isContentEnabled - If tariffs are available or not
 * @param isFriendsAndFamily - If customer has F&F
 * @param isYoung - If customer chose young option
 * @param isTVProductSelected - If customer chose the tv product
 */
export const updatePriceElements = (
    offerSummaryCard: OfferSummaryCard | null,
    tariff: JarvisProduct | undefined,
    tvProduct: JarvisTvProduct | undefined,
    isContentEnabled: boolean,
    isFriendsAndFamily: boolean,
    isYoung: boolean,
    isTVProductSelected: boolean,
) => {
    offerSummaryCard?.updatePrices(
        getPromoPrices(tariff, tvProduct, isYoung, isFriendsAndFamily, isTVProductSelected, isContentEnabled),
        getAdditionalPrices(tariff, isFriendsAndFamily, isYoung, isTVProductSelected, tvProduct),
    );

    offerSummaryCard?.updateLegalText(getLegalTexts(tariff, isFriendsAndFamily));
};

/**
 * Updates highlight badge and toggle visibility due to bug in offer summary card component
 * Order of update and toggle is important
 *
 * @param offerSummaryCard - The OfferSummaryCard component to update
 * @param tariff - The selected tariff
 * @param isContentEnabled - If tariffs are available or not
 */
export const updateHighlightBadge = (
    offerSummaryCard: OfferSummaryCard | null,
    tariff: JarvisProduct | undefined,
    isContentEnabled: boolean,
) => {
    const highlightBadgeProps = getHighlightBadgeProps(isContentEnabled, tariff?.onlineBonus);

    offerSummaryCard?.updateHighlightBadge(highlightBadgeProps ?? {});
    offerSummaryCard?.toggleHighlightBadgeContainer(!!highlightBadgeProps);
};

/**
 * Updates the headline of the OfferSummaryCard component
 *
 * @param offerSummaryCard - The OfferSummaryCard component to update
 * @param isContentEnabled - If tariffs are available or not
 */
export const updateHeadlineFootnoteLink = (offerSummaryCard: OfferSummaryCard | null, isContentEnabled: boolean) => {
    const footnoteLinkNumber = getCMSConfig().offerSummaryCard.headlineFootnoteNumber;

    if (!offerSummaryCard || !isContentEnabled || !footnoteLinkNumber) {
        return;
    }

    const headlineEl = getElementByBrixClassName(offerSummaryCard, HEADLINE_BASE_CLASSNAME);

    headlineEl && updateFootnoteLink(headlineEl, footnoteLinkNumber);
};

/**
 * Adds tooltip tracking event to the Hightlight Badge component
 *
 * @param offerSummaryCard - Offer Summary Card brix component
 */
export const addHighlightBadgeTooltipTracking = (offerSummaryCard: OfferSummaryCard | null) => {
    const tooltipProps = offerSummaryCard?.getProperties().containerHighlightBadge?.containerTooltip;

    tooltipProps && addTooltipTrackingEvent(tooltipProps.stdTooltipId);
};

/**
 * Updates all OfferSummaryCard elements
 *
 * @param offerSummaryCard - The OfferSummaryCard component to update
 * @param state - Current state of the application
 * @param onClick - On click action to handle overlay opening
 */
export const updateOfferSummaryCard = (
    offerSummaryCard: OfferSummaryCard | null,
    state: State,
    onClick: () => void,
) => {
    const {
        selectedTariff,
        isContentEnabled,
        isAvailabilityChecked,
        isFriendsAndFamily,
        isGigakombi,
        isYoung,
        isTVProductSelected,
        alternativeTariff,
        selectedTVProduct,
        customer,
    } = state;

    if (!offerSummaryCard) {
        return;
    }

    offerSummaryCard.updateHeadline(getOfferSummaryCardHeadlineProps(isContentEnabled, selectedTariff?.name));
    offerSummaryCard.updateSubline([getSublineText(isContentEnabled, selectedTariff)]);

    offerSummaryCard.updateButtons([
        getOfferSummaryCardButtonProps(
            selectedTariff || alternativeTariff,
            isGigakombi,
            isYoung,
            isAvailabilityChecked,
            isTVProductSelected,
            Boolean(customer?.existingKipId),
            onClick,
        ),
    ]);

    updateHeadlineFootnoteLink(offerSummaryCard, isContentEnabled);
    updateHighlightBadge(offerSummaryCard, selectedTariff, isContentEnabled);

    updatePriceElements(
        offerSummaryCard,
        selectedTariff,
        selectedTVProduct,
        isContentEnabled,
        isFriendsAndFamily,
        isYoung,
        isTVProductSelected,
    );

    updateDisplayMetacode(offerSummaryCard, selectedTariff?.id);
    addHighlightBadgeTooltipTracking(offerSummaryCard);
};

/**
 * Create the Cable Connection (ÜP2) module for the Offer Summary Card component.
 *
 * @param offerSummaryCard - Offer Summary Card Brix component.
 * @param isAvailabilityChecked - If AVC is done.
 * @param isCableConnection - Is the checked address ÜP2?
 */
export const updateCableConnectionModule = (
    offerSummaryCard: OfferSummaryCard | null | undefined,
    isAvailabilityChecked: boolean,
    isCableConnection: boolean,
) => {
    if (!offerSummaryCard) {
        return;
    }

    offerSummaryCard.toggleContainer(!isAvailabilityChecked || !isCableConnection);
};
